import React, { useState, useEffect } from "react";
import { Text, View, StyleSheet, TouchableOpacity, ScrollView, Platform, Dimensions } from "react-native";
import { FontAwesome6 } from '@expo/vector-icons';

const { width: screenWidth, height: screenHeight } = Dimensions.get("window");
const isSmallScreen = screenWidth < 375;
const isLargeScreen = screenWidth >= 390;

import Home from "./app/components/Screen_Simple";
import About from "./app/components/Screen_About";
import Contact from "./app/components/Screen_Contact";
import Settings from "./app/components/Screen_Settings";
import Privacy from "./app/components/Screen_Privacy";
import Support from "./app/components/Screen_Support";
import HealthWarningModal from "./app/components/HealthWarningModal";
import { ThemeProvider, useTheme } from "./app/components/ThemeContext";
import { getData, storeData } from "./app/components/storage";
import WebAnalytics from "./app/components/WebAnalytics";

function AppWithTheme() {
  const [currentScreen, setCurrentScreen] = useState('home');
  const [menuVisible, setMenuVisible] = useState(false);
  const [red670nmEnabled, setRed670nmEnabled] = useState(false);
  const [showHealthWarning, setShowHealthWarning] = useState(false);
  const { themeValues, changeTheme } = useTheme();

  // Handle URL routing for direct navigation
  useEffect(() => {
    if (Platform.OS === 'web') {
      const path = window.location.pathname;
      if (path === '/privacy') {
        setCurrentScreen('privacy');
      } else if (path === '/support') {
        setCurrentScreen('support');
      } else if (path === '/about') {
        setCurrentScreen('about');
      } else if (path === '/contact') {
        setCurrentScreen('contact');
      } else if (path === '/settings') {
        setCurrentScreen('settings');
      }
    }
  }, []);

  // Update URL when screen changes
  useEffect(() => {
    if (Platform.OS === 'web') {
      const pathMap = {
        'home': '/',
        'about': '/about',
        'privacy': '/privacy',
        'support': '/support',
        'contact': '/contact',
        'settings': '/settings'
      };

      const newPath = pathMap[currentScreen] || '/';
      if (window.location.pathname !== newPath) {
        window.history.pushState({}, '', newPath);
      }
    }
  }, [currentScreen]);

  useEffect(() => {
    // Load saved settings immediately
    const loadSettings = async () => {
      try {
        const settings = await getData();
        console.log('Loaded settings:', settings);
        if (settings) {
          if (settings.red670nmEnabled !== undefined) {
            console.log('Setting red670nm to:', settings.red670nmEnabled);
            setRed670nmEnabled(settings.red670nmEnabled);
          }
          if (settings.theme) {
            console.log('Setting theme to:', settings.theme);
            changeTheme(settings.theme);
          }
          // Check if health warning has been acknowledged
          if (!settings.healthWarningAcknowledged) {
            setShowHealthWarning(true);
          }
        }
      } catch (error) {
        console.log('Error loading settings:', error);
        // Show health warning on error (first time)
        setShowHealthWarning(true);
      }
    };
    loadSettings();

    // Set document title for web version
    if (Platform.OS === 'web') {
      document.title = 'MeditRate';
    }
  }, []);

  const handleHealthWarningAcknowledge = async () => {
    try {
      await storeData({ healthWarningAcknowledged: true });
      setShowHealthWarning(false);
    } catch (error) {
      console.log('Error saving health warning acknowledgment:', error);
      // Still close the modal even if save fails
      setShowHealthWarning(false);
    }
  };

  return (
    <>
    <View style={[styles.container, { backgroundColor: themeValues.background }]}>
      {currentScreen === 'home' ? (
        <View style={styles.screenContainer}>
          <Home red670nmEnabled={red670nmEnabled} />
          <View style={styles.headerLeft}>
            <TouchableOpacity
              style={[styles.menuButton, { backgroundColor: themeValues.secondary }]}
              onPress={() => setMenuVisible(!menuVisible)}
            >
              <FontAwesome6 name="bars" size={20} color={themeValues.text} />
            </TouchableOpacity>
            {Platform.OS === 'web' && (
              <FontAwesome6 name="heart-pulse" size={24} color="#CC0000" />
            )}
            {Platform.OS === 'web' && (
              <Text style={[styles.appTitle, { color: '#CC0000', fontSize: Platform.OS === 'web' ? 36 : 18 }]}>MeditRate.com</Text>
            )}
          </View>

          {menuVisible && (
            <View style={styles.menuOverlay}>
              <TouchableOpacity
                style={styles.menuBackdrop}
                onPress={() => setMenuVisible(false)}
                activeOpacity={1}
              />
              <View style={[styles.menu, { backgroundColor: themeValues.secondary }]}>
                <View style={styles.menuHeader}>
                  <TouchableOpacity onPress={() => setMenuVisible(false)}>
                    <FontAwesome6 name="bars" size={20} color={themeValues.text} />
                  </TouchableOpacity>
                  <FontAwesome6 name="heart-pulse" size={24} color="#CC0000" style={{ marginLeft: Platform.OS === 'web' ? 15 : 0 }} />
                  <Text style={[styles.appTitle, { color: '#CC0000', fontSize: Platform.OS === 'web' ? 36 : 16, marginLeft: Platform.OS === 'web' ? 5 : 0 }]}>MeditRate.com</Text>
                </View>
                <TouchableOpacity
                  style={styles.menuItem}
                  onPress={() => {
                    setMenuVisible(false);
                    setCurrentScreen('about');
                  }}
                >
                  <FontAwesome6 name="info" size={16} color={themeValues.text} style={{ marginRight: 12, width: 20 }} />
                  <Text style={[styles.menuItemText, { color: themeValues.text }]}>About</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.menuItem}
                  onPress={() => {
                    setMenuVisible(false);
                    setCurrentScreen('contact');
                  }}
                >
                  <FontAwesome6 name="envelope" size={16} color={themeValues.text} style={{ marginRight: 12, width: 20 }} />
                  <Text style={[styles.menuItemText, { color: themeValues.text }]}>Contact Us</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.menuItem}
                  onPress={() => {
                    setMenuVisible(false);
                    setCurrentScreen('settings');
                  }}
                >
                  <FontAwesome6 name="gear" size={16} color={themeValues.text} style={{ marginRight: 12, width: 20 }} />
                  <Text style={[styles.menuItemText, { color: themeValues.text }]}>Settings</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.menuItem}
                  onPress={() => {
                    setMenuVisible(false);
                    setCurrentScreen('privacy');
                  }}
                >
                  <FontAwesome6 name="shield" size={16} color={themeValues.text} style={{ marginRight: 12, width: 20 }} />
                  <Text style={[styles.menuItemText, { color: themeValues.text }]}>Privacy</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.menuItem}
                  onPress={() => {
                    setMenuVisible(false);
                    setCurrentScreen('support');
                  }}
                >
                  <FontAwesome6 name="life-ring" size={16} color={themeValues.text} style={{ marginRight: 12, width: 20 }} />
                  <Text style={[styles.menuItemText, { color: themeValues.text }]}>Support</Text>
                </TouchableOpacity>
              </View>
            </View>
          )}
        </View>
      ) : currentScreen === 'about' ? (
        <View style={styles.screenContainer}>
          <About />
          <TouchableOpacity
            style={[styles.backButton, { backgroundColor: themeValues.secondary, top: Platform.OS === 'ios' ? (isSmallScreen ? 40 : (isLargeScreen ? 80 : 70)) : 50 }]}
            onPress={() => setCurrentScreen('home')}
          >
            <Text style={[styles.backButtonText, { color: themeValues.text }]}>← Back</Text>
          </TouchableOpacity>
        </View>
      ) : currentScreen === 'contact' ? (
        <View style={styles.screenContainer}>
          <Contact />
          <TouchableOpacity
            style={[styles.backButton, { backgroundColor: themeValues.secondary, top: Platform.OS === 'ios' ? (isSmallScreen ? 30 : 60) : 50 }]}
            onPress={() => setCurrentScreen('home')}
          >
            <Text style={[styles.backButtonText, { color: themeValues.text }]}>← Back</Text>
          </TouchableOpacity>
        </View>
      ) : currentScreen === 'settings' ? (
        <View style={styles.screenContainer}>
          <Settings
            onNavigateBack={() => setCurrentScreen('home')}
            onRed670nmChange={setRed670nmEnabled}
            red670nmEnabled={red670nmEnabled}
          />
          <TouchableOpacity
            style={[styles.backButton, { backgroundColor: themeValues.secondary }]}
            onPress={() => setCurrentScreen('home')}
          >
            <Text style={[styles.backButtonText, { color: themeValues.text }]}>← Back</Text>
          </TouchableOpacity>
        </View>
      ) : currentScreen === 'privacy' ? (
        <View style={styles.screenContainer}>
          <Privacy />
          <TouchableOpacity
            style={[styles.backButton, { backgroundColor: themeValues.secondary, top: Platform.OS === 'ios' ? (isSmallScreen ? 40 : (isLargeScreen ? 80 : 70)) : 50 }]}
            onPress={() => setCurrentScreen('home')}
          >
            <Text style={[styles.backButtonText, { color: themeValues.text }]}>← Back</Text>
          </TouchableOpacity>
        </View>
      ) : currentScreen === 'support' ? (
        <View style={styles.screenContainer}>
          <Support />
          <TouchableOpacity
            style={[styles.backButton, { backgroundColor: themeValues.secondary, top: Platform.OS === 'ios' ? (isSmallScreen ? 40 : (isLargeScreen ? 80 : 70)) : 50 }]}
            onPress={() => setCurrentScreen('home')}
          >
            <Text style={[styles.backButtonText, { color: themeValues.text }]}>← Back</Text>
          </TouchableOpacity>
        </View>
      ) : null}
    </View>

    <HealthWarningModal
      visible={showHealthWarning}
      onAcknowledge={handleHealthWarningAcknowledge}
    />
    </>
  );
}

export default function App() {
  const [VercelAnalytics, setVercelAnalytics] = React.useState(null);
  const [SpeedInsights, setSpeedInsights] = React.useState(null);

  React.useEffect(() => {
    // Only load Vercel Analytics on web platform
    if (Platform.OS === 'web') {
      // Create the component inline to avoid bundling issues
      const loadVercelAnalytics = async () => {
        try {
          const { Analytics } = await import('@vercel/analytics/react');
          const { SpeedInsights } = await import('@vercel/speed-insights/react');
          setVercelAnalytics(() => Analytics);
          setSpeedInsights(() => SpeedInsights);
        } catch (error) {
          console.log('Vercel Analytics not available:', error);
        }
      };
      loadVercelAnalytics();
    }
  }, []);

  return (
    <ThemeProvider>
      <AppWithTheme />
      {WebAnalytics && <WebAnalytics />}
      {Platform.OS === 'web' && VercelAnalytics && <VercelAnalytics />}
      {Platform.OS === 'web' && SpeedInsights && <SpeedInsights />}
    </ThemeProvider>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'black',
  },
  screenContainer: {
    flex: 1,
  },
  menuButton: {
    paddingHorizontal: 15,
    paddingVertical: 12,
    borderRadius: 20,
    zIndex: 1000,
  },
  headerLeft: {
    position: 'absolute',
    top: 50,
    left: 20,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 15,
  },
  appTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  menuOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 999,
  },
  menuBackdrop: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  menu: {
    position: 'absolute',
    top: 45,
    left: 20,
    backgroundColor: '#333',
    borderRadius: 8,
    minWidth: Platform.OS === 'web' ? 200 : 150,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
  },
  menuHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 20,
    paddingVertical: 15,
    borderBottomWidth: 1,
    borderBottomColor: '#444',
  },
  menuTitle: {
    fontSize: 16,
    fontWeight: '600',
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 15,
    borderBottomWidth: 1,
    borderBottomColor: '#444',
  },
  menuItemText: {
    color: 'white',
    fontSize: 16,
  },
  backButton: {
    position: 'absolute',
    top: Platform.OS === 'ios' ? 70 : 50,
    left: 20,
    backgroundColor: '#333',
    paddingHorizontal: 15,
    paddingVertical: 10,
    borderRadius: 20,
    zIndex: 1000,
  },
  backButtonText: {
    color: 'white',
    fontSize: 16,
  }
});
